import PropTypes from 'prop-types'
import { Lock } from 'lucide-react'
import { useHistory } from 'react-router-dom'
import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { useUserStore } from '@caradasstore/UserStore'
import { planLimitRefusalOf } from '../../data/entitlements'
import { tierById } from '../../data/catalog'
import { useCatalog } from '../../store/CatalogStore'
import { Card, DarkButton, Eyebrow, OutlineButton, Pill } from './BillingUi'

const BILLING_PATH = '/settings?section=billing'

/** The RO_LOCKED refusal of an axios error (`{ code, message, requiredTier, summary }`), or null. */
export const roLockOf = (error) => {
  const refusal = planLimitRefusalOf(error)
  return refusal?.code === 'RO_LOCKED' ? refusal : null
}

/** A blurred value for list cells of a locked RO; the real number is never sent, this is the placeholder. */
export const LockedValue = ({ children = '••••', title = 'Locked by your plan' }) => (
  <span className='inline-flex items-center gap-1 text-muted-foreground' title={title} aria-label={title}>
    <Lock className='h-3.5 w-3.5' aria-hidden='true' />
    <span className='select-none blur-[3px]'>{children}</span>
  </span>
)
LockedValue.propTypes = { children: PropTypes.node, title: PropTypes.string }

/** The small pill lists show on a row whose RO is locked by the plan. */
export const PlanLockedPill = ({ className = '' }) => <Pill label='Locked by plan' tone='amber' dot={false} className={className} />
PlanLockedPill.propTypes = { className: PropTypes.string }

/**
 * What a detail page shows in place of an estimate / quote whose repair order is locked by the plan's
 * monthly RO limit (403 RO_LOCKED): what the job is (RO, client, vehicle — never the results), why, and the
 * plan that unlocks it. Owners and managers get a button to Billing; others are told who to ask.
 */
export const RoLockedView = ({ refusal, onBack }) => {
  const history = useHistory()
  const user = useUserStore((store) => store.currentUser)
  const { catalog } = useCatalog()
  const canManage = Boolean(user && (user.is_owner || user.is_manager || user.is_admin))
  const summary = refusal?.summary || {}
  const upgrade = tierById(catalog, refusal?.requiredTier)
  return (
    <AdasBox className='mx-auto mt-10 max-w-[640px]'>
      <Card className='px-8 py-10 text-center'>
        <AdasBox className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FEF3C7]'>
          <Lock className='h-5 w-5 text-[#B45309]' aria-hidden='true' />
        </AdasBox>
        <Eyebrow>Locked by your plan</Eyebrow>
        <AdasTypography className='mt-1 text-[22px] font-bold text-foreground'>
          {summary.roNumber ? `Repair order ${summary.roNumber}` : 'This repair order'} is locked
        </AdasTypography>
        {(summary.client || summary.vehicle) && (
          <AdasTypography className='mt-2 text-[14px] text-muted-foreground'>
            {[summary.client, summary.vehicle].filter(Boolean).join(' · ')}
            {summary.quoteReference ? ` · quote ${summary.quoteReference}` : ''}
          </AdasTypography>
        )}
        <AdasTypography className='mt-4 text-[14px] text-foreground'>
          {refusal?.message || 'This repair order came in after the location passed its plan’s monthly limit, so its results are locked.'}
        </AdasTypography>
        <AdasTypography className='mt-2 text-[13px] text-muted-foreground'>
          The estimate was processed and is kept; it opens as soon as the plan allows it.
          {!canManage && upgrade ? ` Ask your account owner about ${upgrade.name}.` : ''}
        </AdasTypography>
        <AdasBox className='mt-6 flex flex-wrap justify-center gap-3'>
          {canManage && (
            <DarkButton onClick={() => history.push(BILLING_PATH)}>{upgrade ? `Upgrade to ${upgrade.name}` : 'Open Billing'}</DarkButton>
          )}
          <OutlineButton onClick={onBack || (() => history.goBack())}>Back</OutlineButton>
        </AdasBox>
      </Card>
    </AdasBox>
  )
}
RoLockedView.propTypes = {
  refusal: PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
    requiredTier: PropTypes.string,
    summary: PropTypes.object
  }),
  onBack: PropTypes.func
}
